import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import moment from 'moment';
import Stepper from '../../../core/components/Stepper';
import { DrawerControls } from '../../../core/components/Drawer';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import CustomSelectInput from '../../../core/components/CustomSelectInput';
import { requiredInput, requiredSelect } from '../../../core/formValidation';
import { apiError, generateToastr } from '../../../core/toastMessages';
import {
  TaskTemplate,
  useCreateTaskTemplateMutation,
  useEnsureTaskOccurrenceMutation,
  useUpdateOccurrenceMutation,
  useUpdateTaskTemplateMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import { selectTaskLocations, selectTaskPrefixes } from '../../tasks/reducer';
import { getTaskLocations, getTaskPrefixes } from '../../tasks/actions';
import { selectAssetTemplateOccurrences } from '../../assets/reducer';
import { fetchAssetTemplateOccurrences } from '../../assets/actions';
import { formatTemplateType } from '../../assets/models';
import { formatLookupOptions } from '../helpers';
import { TaskDetails } from './TaskDetails';
import { TaskInstructionsTab } from './TaskInstructionsTab';

interface EssentialValues {
  name: string;
  description: string | null;
  /** This site's copy of the template, not the master template. */
  assetTemplateOccurrenceId: number | null;
  locationId: number | null;
  prefixId: number | null;
}

interface WorkOrderCreateWizardProps {
  /** A work order saved earlier with Save & finish later, to pick up where it was left. */
  draft?: TaskTemplate | null;
  maximized?: boolean;
  /** Omitted where the drawer can't be maximized (narrow screens). */
  onToggleMaximize?: () => void;
  onClose: () => void;
}

const ESSENTIAL_STEP = 0;
const ADDITIONAL_STEP = 1;

const hasText = (html: string | null) =>
  !!html &&
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

/**
 * Add Work Order in two steps. Essential creates the work order from the few
 * fields it needs; Additional then opens the full editor on what was created.
 */
const WorkOrderCreateWizard: React.FC<WorkOrderCreateWizardProps> = ({
  draft = null,
  maximized = false,
  onToggleMaximize,
  onClose,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const taskLocations = useSelector(selectTaskLocations);
  const taskPrefixes = useSelector(selectTaskPrefixes);
  const templateOccurrences = useSelector(selectAssetTemplateOccurrences) as any[];

  const [step, setStep] = useState(ESSENTIAL_STEP);
  const [created, setCreated] = useState<TaskTemplate | null>(null);
  const [descriptionError, setDescriptionError] = useState(false);
  const [createTask, { isLoading: isCreating }] = useCreateTaskTemplateMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskTemplateMutation();
  const [ensureOccurrence] = useEnsureTaskOccurrenceMutation();
  const [updateOccurrence] = useUpdateOccurrenceMutation();
  const [savingForLater, setSavingForLater] = useState(false);
  const isSaving = isCreating || isUpdating;
  const savedRef = useRef<TaskTemplate | null>(draft);

  // A draft's template link lives on its occurrence, like a finished work order's.
  const draftAssetLink: number | null =
    (draft?.latestOccurrence as any)?.assetTemplateOccurrenceId ?? null;

  const form = useForm<EssentialValues>({
    defaultValues: {
      name: draft?.name ?? '',
      description: draft?.description ?? null,
      assetTemplateOccurrenceId: draftAssetLink,
      locationId: draft?.locationId ?? null,
      prefixId: draft?.prefixId ?? null,
    },
  });

  useEffect(() => {
    if (!taskLocations?.length) dispatch(getTaskLocations());
    if (!taskPrefixes?.length) dispatch(getTaskPrefixes());
    if (!templateOccurrences.length) dispatch(fetchAssetTemplateOccurrences());
  }, []);

  const templateOptions = useMemo(
    () =>
      templateOccurrences
        .filter((occurrence) => !occurrence.archivedAt)
        .map((occurrence) => {
          const name = occurrence.name || occurrence.assetTemplate?.name || '';
          const type = occurrence.assetTemplate?.type;
          return {
            value: occurrence.id,
            label: type ? `${name} (${formatTemplateType(type)})` : name,
          };
        }),
    [templateOccurrences]
  );

  // Unfilled fields go up as null so a draft can be saved before they are known.
  const buildBody = (values: EssentialValues, incomplete: boolean) => {
    return {
      name: values.name.trim(),
      description: hasText(values.description) ? values.description : null,
      type: 'Project',
      interval: 'Once',
      intervalCount: 1,
      // A one-time work order fires on its start date, so finishing a draft
      // moves it to the day it actually goes live.
      start: moment().startOf('day').toISOString(),
      locationId: values.locationId ?? null,
      prefixId: values.prefixId ?? null,
      // Master asset links would make this a blueprint task that deploys copy to
      // every site, so they stay empty; the site link goes on the occurrence.
      assetTemplateId: null,
      assetGroupId: null,
      assetItemId: null,
      incomplete,
      ...(savedRef.current ? {} : { priority: 'Low', alertIfOverdue: true }),
    };
  };

  // Same as linking a task from the asset page: the work order's occurrence at
  // this site carries the site-copy ids. Through RTK so an expired token is
  // refreshed and the work order lists pick up the new link.
  const linkToAsset = async (taskId: number, values: EssentialValues) => {
    const ensured = await ensureOccurrence(taskId).unwrap();
    const occurrenceId = ensured?.occurrence?.id;
    if (occurrenceId == null) return;
    await updateOccurrence({
      occurrenceId,
      assetTemplateOccurrenceId: values.assetTemplateOccurrenceId ?? null,
    }).unwrap();
  };

  // Links when a template is picked, and unlinks a draft whose template was cleared.
  const syncAssetLink = async (taskId: number, values: EssentialValues) => {
    if (values.assetTemplateOccurrenceId || draftAssetLink) await linkToAsset(taskId, values);
  };

  const save = async (values: EssentialValues, incomplete: boolean): Promise<TaskTemplate> => {
    const body: any = buildBody(values, incomplete);
    const existing = savedRef.current;
    const saved = existing
      ? await updateTask({ id: existing.id, ...body }).unwrap()
      : await createTask(body).unwrap();
    savedRef.current = saved;
    return saved;
  };

  const submitEssential = async (values: EssentialValues) => {
    if (!hasText(values.description)) {
      setDescriptionError(true);
      return;
    }
    let saved: TaskTemplate;
    try {
      saved = await save(values, false);
    } catch (e) {
      apiError(e);
      return;
    }
    // The work order exists at this point, so a failed link is reported but
    // does not hold the user on this step.
    await syncAssetLink(saved.id, values).catch(apiError);
    setCreated(saved);
    setStep(ADDITIONAL_STEP);
  };

  // Like a claim, a draft needs just enough to be found again: here, its name.
  const saveForLater = async () => {
    const values = form.getValues();
    if (!values.name.trim()) {
      form.setError('name', { type: 'required' });
      generateToastr(
        'warning',
        intl.formatMessage({ id: 'workOrderWizard.moreInfoNeeded' }),
        intl.formatMessage({ id: 'workOrderWizard.saveLaterNeedsName' })
      );
      return;
    }
    setSavingForLater(true);
    let saved: TaskTemplate;
    try {
      saved = await save(values, true);
    } catch (e) {
      apiError(e);
      setSavingForLater(false);
      return;
    }
    // The draft exists now, so a failed link is reported without holding the
    // user here: it can be set again when the draft is reopened.
    const linked = await syncAssetLink(saved.id, values).then(
      () => true,
      () => false
    );
    setSavingForLater(false);
    if (linked) {
      generateToastr(
        'success',
        intl.formatMessage({ id: 'workOrderWizard.savedForLater' }),
        intl.formatMessage({ id: 'workOrderWizard.savedForLaterMessage' })
      );
    } else {
      generateToastr(
        'warning',
        intl.formatMessage({ id: 'workOrderWizard.savedForLater' }),
        intl.formatMessage({ id: 'workOrderWizard.savedLinkFailed' })
      );
    }
    onClose();
  };

  // Description lives in the rich-text editor, which has no rules of its own,
  // so it is checked alongside the form's validation rather than inside it.
  const handleCreate = () => {
    setDescriptionError(!hasText(form.getValues('description')));
    form.handleSubmit(submitEssential)();
  };

  const steps = [
    intl.formatMessage({ id: 'workOrderWizard.essential', defaultMessage: 'Essential' }),
    intl.formatMessage({ id: 'workOrderWizard.additional', defaultMessage: 'Additional' }),
  ];
  const stepper = (
    <Stepper
      className="work-order-wizard__steps"
      steps={steps}
      currentStep={step}
      ariaLabel={intl.formatMessage({
        id: 'workOrderWizard.progress',
        defaultMessage: 'Add work order progress',
      })}
    />
  );

  const maximizeLabel = intl.formatMessage({ id: maximized ? 'minimizeDrawer' : 'maximizeDrawer' });

  if (step === ADDITIONAL_STEP && created) {
    return (
      <div className="work-order-wizard">
        <TaskDetails
          template={created}
          isEditing
          onClose={onClose}
          onSave={onClose}
          leadingControls={
            onToggleMaximize && (
              <button
                type="button"
                onClick={onToggleMaximize}
                className="button drawer-maximize"
                title={maximizeLabel}
              >
                <i className={`uil ${maximized ? 'uil-compress' : 'uil-focus'}`} />
              </button>
            )
          }
          subHeader={stepper}
        />
      </div>
    );
  }

  return (
    <div className="work-order-wizard task-details-drawer">
      <div className="work-order-wizard__header">
        <DrawerControls
          closeFunction={onClose}
          maximizeFunction={onToggleMaximize ?? null}
          maximized={maximized}
        />
        <div className="work-order-wizard__actions">
          <ButtonWithLoader
            handleClick={saveForLater}
            isLoading={savingForLater}
            disabled={isSaving}
            style="button"
          >
            <>
              {intl.formatMessage({
                id: 'workOrderWizard.saveFinishLater',
                defaultMessage: 'Save & finish later',
              })}
            </>
          </ButtonWithLoader>
          <ButtonWithLoader
            handleClick={handleCreate}
            isLoading={isSaving && !savingForLater}
            disabled={isSaving}
            style="button primary"
          >
            <>
              {intl.formatMessage({
                id: 'workOrderWizard.create',
                defaultMessage: 'Create work order',
              })}
            </>
          </ButtonWithLoader>
        </div>
      </div>
      {stepper}
      <FormProvider {...form}>
        <form
          className="work-order-wizard__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <div className="form-group padded">
            <label htmlFor="name">{intl.formatMessage({ id: 'taskName' })}</label>
            <Controller
              name="name"
              control={form.control}
              rules={{ required: true, validate: (value) => value.trim().length > 0 }}
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'name',
                  type: 'text',
                  placeholder: intl.formatMessage({ id: 'workOrderName' }),
                })
              }
            />
          </div>

          <div className="form-group padded">
            <label htmlFor="description">
              {intl.formatMessage({ id: 'description', defaultMessage: 'Description' })}
            </label>
            <TaskInstructionsTab task={draft as any} editing />
            {descriptionError && !hasText(form.watch('description')) && (
              <span className="work-order-wizard__error">
                {intl.formatMessage({
                  id: 'workOrderWizard.descriptionRequired',
                  defaultMessage: 'Description is required',
                })}
              </span>
            )}
          </div>

          <div className="form-group padded">
            <label htmlFor="assetTemplateOccurrenceId">
              {intl.formatMessage({ id: 'workOrderWizard.asset', defaultMessage: 'Asset' })}
            </label>
            <Controller
              name="assetTemplateOccurrenceId"
              control={form.control}
              // Asset is optional for now; restore this to require it again.
              // rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <CustomSelectInput
                  options={templateOptions}
                  value={templateOptions.find((option) => option.value === value) ?? null}
                  onChange={(option: any) => onChange(option ? option.value : null)}
                  placeholder={intl.formatMessage({
                    id: 'workOrderWizard.assetPlaceholder',
                    defaultMessage: 'Select a template…',
                  })}
                  isClearable
                  multi={false}
                />
              )}
            />
            {form.formState.errors.assetTemplateOccurrenceId && (
              <span className="work-order-wizard__error">
                {intl.formatMessage({
                  id: 'workOrderWizard.assetRequired',
                  defaultMessage: 'Asset is required',
                })}
              </span>
            )}
          </div>

          <div className="form-group padded">
            <label htmlFor="locationId">{intl.formatMessage({ id: 'location' })}</label>
            <Controller
              name="locationId"
              control={form.control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: {
                    value,
                    onChange: (e: any) => {
                      const raw = e?.target?.value ?? e;
                      onChange(raw ? Number(raw) : null);
                    },
                  },
                  id: 'locationId',
                  options: formatLookupOptions(taskLocations),
                })
              }
            />
          </div>

          <div className="form-group padded">
            <label htmlFor="prefixId">{intl.formatMessage({ id: 'taskPrefix' })}</label>
            <Controller
              name="prefixId"
              control={form.control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: {
                    value,
                    onChange: (e: any) => {
                      const raw = e?.target?.value ?? e;
                      onChange(raw ? Number(raw) : null);
                    },
                  },
                  id: 'prefixId',
                  options: formatLookupOptions(taskPrefixes),
                })
              }
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default WorkOrderCreateWizard;
